import { useKnockClient } from "@knocklabs/react";
import { isDefined } from "@clipboard-health/util-ts";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { Platform } from "@src/mobile/utils/platform";
import { useEffect } from "react";

import { KNOCK_FIRST_MOBILE_APP_USE_AT_ATTRIBUTE } from "./constants";

/**
 * Writes `firstMobileAppUseAt` to the Knock user record on the first mobile
 * session that finds the attribute absent. Read by the Knock dashboard's
 * `daysSince(firstMobileAppUseAt)` targeting rule to enforce a mobile-soak
 * floor before any prompt fires.
 *
 * The "is it already set?" check goes through `user.get()` so we don't
 * pollute the user record with a redundant write on every mount.
 *
 * Tenure is no longer emitted from the client — the Knock dashboard reads
 * `recipient.createdAt` (Knock's own first-identify timestamp) for that.
 *
 * Best-effort emit: a failed call is logged and dropped; the next mount
 * (or `workplaceUserId` change) will retry. Must be called inside a live
 * `KnockProvider`; the mount component handles that.
 */
export function useEmitReviewPromptAttributes(): void {
  const knockClient = useKnockClient();
  const session = useSession();
  const workplaceUserId = session?.profile?.userId;

  useEffect(() => {
    if (!Platform.isMobile || !isDefined(workplaceUserId)) {
      // firstMobileAppUseAt is mobile-only by definition; nothing to write
      // on web or before the session profile is ready. No round-trip either.
      return;
    }

    void (async () => {
      try {
        const user = await knockClient.user.get();
        // Broad falsy check is deliberate: `User extends GenericData` types
        // arbitrary keys as `any`, so an absent key, `null`, or empty string
        // should all be treated as "not yet set, please write."
        if (!user[KNOCK_FIRST_MOBILE_APP_USE_AT_ATTRIBUTE]) {
          await knockClient.user.identify({
            [KNOCK_FIRST_MOBILE_APP_USE_AT_ATTRIBUTE]: new Date().toISOString(),
          });
        }
      } catch (error) {
        logError(APP_V2_APP_EVENTS.RATING_PROMPT_KNOCK_IDENTIFY_FAILED, { error });
      }
    })();
  }, [knockClient, workplaceUserId]);
}
